import { useEffect, useState } from "react";
import { Plus, Trash2, UserPlus, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const selCls = "h-12 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const participantName = (p) => (typeof p === "string" ? p : p.name);
const TYPE_LABEL = { workplace_guest: "Workplace", external_guest: "Invité", team_member: "Équipe" };
const teamEntries = (team) => team.members.map((m) => ({ participant_type: "team_member", user_id: m.id, name: m.name, email: null }));
const isMember = (p, id) => typeof p !== "string" && p.participant_type === "team_member" && p.user_id === id;

const GuestDialog = ({ open, onClose, onAdd }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const add = () => {
    if (!name.trim() || !email.trim()) return setErr("Renseignez le nom / prénom et l'e-mail.");
    if (!EMAIL_RE.test(email.trim())) return setErr("Adresse e-mail invalide.");
    onAdd({ participant_type: "external_guest", name: name.trim(), email: email.trim().toLowerCase() });
    setName(""); setEmail(""); setErr(""); onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="guest-dialog">
        <DialogHeader><DialogTitle>Ajouter un invité</DialogTitle><DialogDescription>Personne extérieure à votre workplace.</DialogDescription></DialogHeader>
        <label htmlFor="guest-name" className="text-sm font-semibold">Nom / Prénom</label>
        <Input id="guest-name" data-testid="guest-name-input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} className="h-12 text-base" />
        <label htmlFor="guest-email" className="text-sm font-semibold">E-mail</label>
        <Input id="guest-email" type="email" data-testid="guest-email-input" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} className="h-12 text-base" />
        {err && <p role="alert" data-testid="guest-error" className="text-sm text-red-400">{err}</p>}
        <DialogFooter><Button data-testid="guest-add-button" onClick={add} className="h-12 w-full text-base font-bold">Ajouter</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WorkplaceSearch = ({ members, exclude, onAdd }) => {
  const [q, setQ] = useState("");
  const s = q.trim().toLowerCase();
  const list = members.filter((m) => m.id && !exclude.has(m.id) && (!s || `${m.name} ${m.email}`.toLowerCase().includes(s))).slice(0, 8);
  return (
    <div className="rounded-md border border-border p-2" data-testid="workplace-search">
      <Input autoFocus data-testid="workplace-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un nom ou un e-mail du workplace" className="h-11 text-base" />
      <div className="mt-2 space-y-1">
        {list.map((m) => (
          <button key={m.id} type="button" data-testid={`workplace-add-${m.id}`} onClick={() => { onAdd(m); setQ(""); }}
            className="flex min-h-[44px] w-full items-center justify-between rounded px-3 text-left text-sm hover:bg-amber-500/10">
            <span className="min-w-0 flex-1">{m.name} <span className="block truncate text-xs text-muted-foreground">{m.email}</span></span><Plus className="h-4 w-4 shrink-0 text-amber-500" />
          </button>
        ))}
        {!list.length && <p className="px-3 py-2 text-sm text-muted-foreground">Aucun membre du workplace ne correspond.</p>}
      </div>
    </div>
  );
};

const Row = ({ checked, onToggle, name, tag, testId }) => (
  <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-md border border-border px-3 transition-colors hover:border-amber-500/60">
    <input type="checkbox" data-testid={testId} className="h-5 w-5 accent-amber-500" checked={checked} onChange={onToggle} />
    <span className="flex-1 text-base">{name}</span>
    {tag && <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">{tag}</span>}
  </label>
);

export const CollectiveParticipants = ({ d, set, members = [] }) => {
  const [teams, setTeams] = useState(null);
  const [search, setSearch] = useState(false);
  const [guest, setGuest] = useState(false);
  useEffect(() => { api.get("/teams/mine").then((r) => setTeams(r.data)).catch(() => setTeams([])); }, []);
  useEffect(() => {
    if (!teams || d.team_id || d.participants.length) return;
    const led = teams.filter((t) => t.is_leader);
    const def = led.length === 1 ? led[0] : teams.length === 1 ? teams[0] : null;
    if (def) set({ team_id: def.id, participants: teamEntries(def) }, "participants");
  }, [teams]); // eslint-disable-line react-hooks/exhaustive-deps

  const team = (teams || []).find((t) => t.id === d.team_id);
  const rows = team ? team.members : [];
  const others = d.participants.filter((p) => !rows.some((m) => isMember(p, m.id)));
  const pick = (id) => {
    const t = (teams || []).find((x) => x.id === id);
    const keep = d.participants.filter((p) => typeof p === "string" || p.participant_type !== "team_member");
    set({ team_id: id || null, participants: [...(t ? teamEntries(t) : []), ...keep] }, "participants");
  };
  const toggle = (m) => set({ participants: d.participants.some((p) => isMember(p, m.id))
    ? d.participants.filter((p) => !isMember(p, m.id))
    : [...d.participants, { participant_type: "team_member", user_id: m.id, name: m.name, email: null }] }, "participants");
  const add = (p) => set({ participants: [...d.participants, p] }, "participants");
  const remove = (p) => set({ participants: d.participants.filter((x) => x !== p) }, "participants");
  const used = new Set([...rows.map((m) => m.id), ...d.participants.map((p) => p.user_id).filter(Boolean)]);

  return (
    <div data-testid="take5-participants-zone" className="space-y-3">
      <p className="text-sm font-semibold">Participants à cette tâche</p>
      {teams && teams.length > 1 && (
        <select data-testid="take5-team-select" aria-label="Équipe" className={selCls} value={d.team_id || ""} onChange={(e) => pick(e.target.value)}>
          <option value="">— Sans équipe —</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}
      {team && (
        <div className="space-y-2" data-testid="take5-team-members">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Membres de l'équipe {teams.length === 1 ? `· ${team.name}` : ""}</p>
          {rows.map((m) => <Row key={m.id} testId={`take5-member-${m.id}`} checked={d.participants.some((p) => isMember(p, m.id))} onToggle={() => toggle(m)}
            name={m.name} tag={m.id === team.leader_id ? "Chef d'équipe" : ""} />)}
        </div>
      )}
      {others.length > 0 && (
        <div className="space-y-2" data-testid="take5-other-participants">
          {others.map((p, i) => (
            <div key={`${p.user_id || p.email || p}-${i}`} data-testid={`take5-participant-${i}`} className="flex min-h-[48px] items-center gap-3 rounded-md border border-border px-3">
              <Check className="h-4 w-4 text-amber-500" />
              <span className="min-w-0 flex-1 text-base">{participantName(p)}{p.email && <span className="block truncate text-xs text-muted-foreground">{p.email}</span>}</span>
              <span className="text-xs text-muted-foreground">{TYPE_LABEL[p.participant_type] || "Participant"}</span>
              <button type="button" aria-label="Retirer" data-testid={`take5-participant-${i}-remove`} onClick={() => remove(p)} className="flex h-10 w-10 items-center justify-center rounded-md text-slate-400 hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {search && <WorkplaceSearch members={members} exclude={used} onAdd={(m) => add({ participant_type: "workplace_guest", user_id: m.id, name: m.name, email: m.email })} />}
      <div className="grid gap-2 sm:grid-cols-2">
        <Button type="button" variant="secondary" data-testid="take5-add-workplace-participant" onClick={() => setSearch((s) => !s)} className="h-11"><Plus className="mr-1 h-4 w-4" /> Ajouter un participant du Workplace</Button>
        <Button type="button" variant="secondary" data-testid="take5-add-guest" onClick={() => setGuest(true)} className="h-11"><UserPlus className="mr-1 h-4 w-4" /> Ajouter un invité</Button>
      </div>
      <GuestDialog open={guest} onClose={() => setGuest(false)} onAdd={add} />
    </div>
  );
};
